import React, { Fragment, useState, useEffect } from "react";
import { backend, API_URL } from "../../../config";
import {
  Button,
  Typography,
  Modal,
  CircularProgress,
  Container,
  Grid,
  TextField,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  layout: {
    width: "auto",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600)]: {
      width: 900,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(5),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

export const PriceRefresh = (props) => {
  const classes = useStyles();

  const cDate = new Date();

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(cDate);
  const [orders, setOrders] = useState([]);
  const [ordersSelected, setOrdersSelected] = useState([]);
  const [syncSummary, setSyncSummary] = useState(null);

  const handleChangeOrders = (e, value) => {
    setOrdersSelected(value);
  };

  const handleChangeDate = (e) => {
    const { value } = e.target;
    setOrders([]);
    setOrdersSelected([]);
    setDeliveryDate(value);
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to update the pricing for the selected orders?
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Refreshing prices <br />
              This may take some time relative to the number of orders selected
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while refreshing prices
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "hidden",
                  display: "inline-block",
                }}
              >
                <b>Error:</b>
                <br />
                {errorInfo.message}
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The selected orders from
          {` ${deliveryDate.toISOString().split("T")[0]} `}
          successfully had their pricing updated.
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  // const toggleResponseModal = () => setResponseModal(!responseModal);

  /* const responseModalContent = (
    <div className={classes.modal}>
      {errorInfo.error ? (
        <>
          There was an error adding the receipt <b>{receiptName}</b>
          <br />
          <br />
          <span
            style={{
              color: "red",
              wordBreak: "break-word",
              maxHeight: 160,
              overflow: "hidden",
              display: "inline-block",
            }}
          >
            <b>Error:</b>
            <br />
            {errorInfo.message}
          </span>
        </>
      ) : (
        <>
          Receipt <b>{receiptName}</b> has been successfully added
        </>
      )}
      <div align="right">
        <Button
          variant="contained"
          color="primary"
          onClick={() => resolveRequest()}
          className={classes.button}
        >
          Continue
        </Button>
      </div>
    </div>
  ); */

  const resolveRequest = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleConfirmModal();
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    const ordersMap = ordersSelected.map((item) => item.id);

    const payloadObj = {
      order_ids: ordersMap,
    };

    await backend
      .post(`${API_URL}orderpricerefresh/`, payloadObj)
      .then(({ data }) => {
        setLoadingFlag(false);
        setFinishedFlag(true);
      })
      .catch((error) => {
        console.log(error);
        const errorData = error.response.data;

        setErrorInfo({
          error: true,
          message:
            typeof errorData === "object"
              ? errorData.hasOwnProperty("message")
                ? errorData.message
                : errorData.detail
              : error.message,
        });
      });
  };

  useEffect(() => {
    const getSyncSummary = async () => {
      await backend.get(`${API_URL}getlatestrdsyncdate`).then(({ data }) => {
        setSyncSummary(data);
      });
    };

    getSyncSummary();
  }, []);

  useEffect(() => {
    const getOrders = async () => {
      let deliveryDateObj = new Date(deliveryDate);

      const deliveryDateParam = new Date(
        deliveryDateObj.getTime() - deliveryDateObj.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .split("T")[0];

      await backend
        .get(
          `${API_URL}orders?no_page=1&start_date=${deliveryDateParam}&end_date=${deliveryDateParam}`,
        )
        .then(({ data }) => {
          if (data.length) {
            setOrders(data);
            setOrdersSelected(data);
          }
        });
    };

    if (deliveryDate !== null) {
      getOrders();
    }
  }, [deliveryDate]);

  return (
    <Container style={{ minHeight: "80vh" }}>
      <Modal open={confirmModal} onClose={toggleConfirmModal}>
        {confirmModalContent}
      </Modal>
      {/* <Modal open={responseModal} onClose={toggleResponseModal}>
            {responseModalContent}
          </Modal> */}
      <Typography component="h6" variant="h6" align="center">
        Price Refresh
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Use this interface to update pricing for all items on the selected
          orders to today's pricing.
          <Typography variant="body2" fontStyle="italic">
            (Normally, pricing for order items are set when the order is placed
            and are only updated if that order item is modified.)
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Load orders from a given date.
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              value={deliveryDate}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Delivery date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeDate({
                  target: {
                    name: "delivery_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        {deliveryDate !== null ? (
          <Grid item xs={12} sm={12}>
            <InputLabel shrink id="orders">
              Orders
            </InputLabel>
            <Autocomplete
              fullWidth
              multiple
              disablePortal
              size="small"
              name="orders"
              options={orders}
              getOptionLabel={(option) =>
                `${option.customer ? option.customer.username : "Anonymous"} #${
                  option.id
                }`
              }
              renderInput={(params) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => handleChangeOrders(e, value)}
              helpertext="Select the orders to include in the receipt difference."
              value={ordersSelected}
            />
          </Grid>
        ) : (
          <></>
        )}
        {syncSummary ? (
          <Grid item xs={12} sm={12} style={{ textAlign: "center" }}>
            <br />
            <b>Last RD Sync Date: </b>
            {syncSummary.latest_sync_date}
            <br />
            <b>Number of Products Updates: </b>
            {syncSummary.num_products_synced}
          </Grid>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12} style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => toggleConfirmModal()}
            className={classes.button}
            disabled={!ordersSelected.length || loadingFlag}
          >
            Update Pricing
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PriceRefresh;
